/**
 * FarmList Page
 * 
 * This page displays a list of farms based on the selected category.
 * It fetches farms by proximity to the user's location and allows toggling between a list view
 * and a map view. The page also handles responsive design for mobile devices.
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FarmListPageStyle from "../../assets/stylesheets/farm-list/FarmListPage.module.css";
import HeaderStyle from "../../assets/stylesheets/Header.module.css";
import { findFarmsByProximity } from "../../api/search-service/searchService"
import { getCategoryById } from "../../api/farm-service/categoryService";
import { useMapView } from "../../hooks/MapViewContext.js";
import useUserLocation from "../../hooks/useUserLocation";
import ListGoogleMapComponent from "../../components/common/map/ListGoogleMap.jsx";
import FarmCardList from "../../components/farm/FarmCardList";
import Pagination from "../../components/common/Pagination";
import Logger from "../../utils/logger";
import slugify from 'slugify';

const log = Logger.create('FarmList');

const COORDINATE_TOLERANCE = 0.000001; // Tolerance for duplicate searches
const VERBOSE = false;
const DEFAULT_PROXIMITY_DISTANCE = 10000; // 10,000 km radius for proximity search
const LIMIT = 12;
const SORT = 'distance'; 
const ORDER = 'ASC';

/**
 * FarmList component displays a list of farms based on the selected category.
 * It fetches farms by proximity to the user's location and allows toggling between a list view and a map view.
 * @returns {JSX.Element} The rendered component.
 * 
 */
export default function FarmList() {
  const navigate = useNavigate();
  const locationObj = useLocation();
  const { categoryId } = useParams();
  const { showMap, setShowMap } = useMapView();

  const [category, setCategory] = useState([]);
  const [farms, setFarms] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({});
  const { location, error: locationError, loading: locationLoading } = useUserLocation();
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 787);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [isMapSearch, setIsMapSearch] = useState(false);
  const [lastSearchLocation, setLastSearchLocation] = useState(null); // Track last search
  const [hoveredFarmId, setHoveredFarmId] = useState(null);
  const [animClass, setAnimClass] = useState("");
  const [mobileNavHeight, setMobileNavHeight] = useState(75); 

  // Read initial page from URL
  const getInitialPage = () => {
    const params = new URLSearchParams(locationObj.search);
    return parseInt(params.get('page'), 10) || 1;
  };
  const getInitialCategory = () => {
    const params = new URLSearchParams(locationObj.search);
    return params.get('category') || categoryId;
  };
  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [currentCategory, setCurrentCategory] = useState(getInitialCategory());

  // Function to search farms by location
  const searchFarmsByLocation = useCallback(async (searchLocation, isFromMap = false, page = 1, options = {}) => {
    const { force = false } = options;

    // Check if searchLocation is valid
    if (!searchLocation) return;
    // Prevent duplicate searches with more forgiving tolerance (only for same page)
    if (
      !force &&
      lastSearchLocation &&
      Math.abs(lastSearchLocation.lat - searchLocation.lat) < COORDINATE_TOLERANCE &&
      Math.abs(lastSearchLocation.lng - searchLocation.lng) < COORDINATE_TOLERANCE &&
      page === currentPage
    ) {
      log.log('Skipping duplicate search for same location and page');
      return;
    }
    log.log('Searching farms at:', searchLocation, isFromMap ? '(from map)' : '(from location)', `page: ${page}`);
    setLoading(true);
    setIsMapSearch(isFromMap);
    setLastSearchLocation(searchLocation);
    // If the search is from the map, update the map center
    try {
      const response = await findFarmsByProximity({
        verbose: VERBOSE,
        latitude: searchLocation.lat,
        longitude: searchLocation.lng,
        distance: DEFAULT_PROXIMITY_DISTANCE,
        categoryId: Number(categoryId),
        page: page,
        limit: LIMIT,
        sort: SORT,
        order: ORDER
      });
      const safeFarms = Array.isArray(response.items) ? response.items : [];
      setFarms(safeFarms);
      setPaginationMeta(response.meta || {});
      setCurrentPage(page);
    } catch (error) {
      log.error('Error searching farms:', error);
      setFarms([]);
      setPaginationMeta({});
    } finally {
      setLoading(false);
    }
  }, [categoryId, lastSearchLocation, currentPage, VERBOSE, DEFAULT_PROXIMITY_DISTANCE, LIMIT, SORT, ORDER]);

  // Function to handle map bounds change
  const handleMapBoundsChanged = (mapData) => {
    log.log('Map bounds changed:', mapData.center);
    searchFarmsByLocation(mapData.center, true, 1); // Reset to page 1 when map bounds change
  };

  // Function to handle page change
  const handlePageChange = useCallback((page) => {
    log.log('Page changed to:', page);
    if (lastSearchLocation) {
      // Use the last search location (user or map center)
      searchFarmsByLocation(lastSearchLocation, isMapSearch, page);
      setCurrentPage(page);
      // Update page and category name in URL
      const params = new URLSearchParams(locationObj.search);
      params.set('page', page);
      // Use category name from loaded category data if available
      let categoryName = category?.data?.name || '';
      if (categoryName) {
        categoryName = categoryName.trim().replace(/\s+/g, '-').toLowerCase();
        params.set('category', categoryName);
      }
      navigate(`${locationObj.pathname}?${params.toString()}`, { replace: true });
    }
    // Scroll to top of the farm list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [lastSearchLocation, isMapSearch, searchFarmsByLocation, locationObj, navigate, category]);

  // Prepare markers for Google Map
  const markers = (farms || [])
    .filter(farm => farm?.location?.coordinates?.lat && farm?.location?.coordinates?.lon)
    .map(farm => ({
      id: farm.id,
      position: {
        lat: farm.location.coordinates.lat,
        lng: farm.location.coordinates.lon
      },
      label: farm.name,
      location: `${farm.location.address?.city || ''}, ${farm.location.address?.state || ''}`,
      image: farm.coverImage?.viewUrl,
      farm: farm 
 
    }));

  // Toggle map visibility and adjust mobile navigation height
  const handleToggle = () => {
    setAnimClass(showMap ? "slide-up" : "slide-down");
    setTimeout(() => {
      setAnimClass("");
      setShowMap(prev => !prev);
      
      // showMap is true → hide map, false → show map
      if (isMobile) {
        setTimeout(() => {
          const mobileNavContainer = document.querySelector(`.${HeaderStyle["mobile-header-container"]}`);
          if (mobileNavContainer) {
            const isHidden = mobileNavContainer.classList.contains(HeaderStyle["hidden"]);
            if (isHidden) {
              // hidden state, use fixed height
              setMobileNavHeight(85);
            } else {
              const height = mobileNavContainer.offsetHeight;
              setMobileNavHeight(height + 20);
            }
          }
        }, 50);
      }
    }, 100);
  };


  /**
   * UseEffect hooks to handle various side effects:
   * 1. Location changes: Search farms when location is available.
   * 2. Category changes: Fetch category details when categoryId changes.
   * 3. Screen resize: Adjust mobile view and map visibility.
   * 4. Mobile navigation height adjustment.
   * 5. URL synchronization: Sync categoryId from URL if it changes.
   * 6. URL synchronization: Ensure page and category name are always in the URL.
   */  
  
  // 1. Handle location effect - search farms when location changes
  // Track previous location and categoryId to avoid unnecessary resets
  const prevLocationRef = React.useRef();
  const prevCategoryIdRef = React.useRef();
  const prevSourceRef = React.useRef();

  useEffect(() => {
    log.log('FarmList: location:', location, 'locationError:', locationError, 'locationLoading:', locationLoading);

    // If still loading location, wait
    if (locationLoading) {
      log.log("Still loading location...");
      return;
    }

    // Only proceed if we have a valid location
    if (!location) {
      log.warn("No location available after loading completed");
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    console.log(`[FarmList][Location] ready from ${source}:`, location);

    if (prevSourceRef.current === 'GPS' && source === 'IP') {
      prevLocationRef.current = location;
      prevCategoryIdRef.current = categoryId;
      prevSourceRef.current = source;
      return;
    }

    // Only reset to page 1 and fetch if location or categoryId actually changed
    const locationChanged =
      prevLocationRef.current?.lat !== location.lat ||
      prevLocationRef.current?.lng !== location.lng;
    const categoryChanged = prevCategoryIdRef.current !== categoryId;

    // If user navigates directly to a page in the URL, use that page
    const params = new URLSearchParams(locationObj.search);
    const urlPage = parseInt(params.get('page'), 10) || 1;
    if ((locationChanged || categoryChanged) && !isMapSearch) {
      setCurrentPage(urlPage);
      searchFarmsByLocation(location, false, urlPage);
    }

    prevLocationRef.current = location;
    prevCategoryIdRef.current = categoryId;
  }, [location, categoryId, locationError, locationLoading, isMapSearch, searchFarmsByLocation, locationObj.search]);

  // 2. Fetch category by ID when categoryId changes
  useEffect(() => {
    if (!categoryId) return;
    setLoading(true);
    setCategory(null);
    getCategoryById(categoryId)
      .then(category => setCategory(category))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [categoryId]);

  // 3. Handle screen resize to toggle mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 787);
      // if the screen is wider than 787px, hide the map
      if (window.innerWidth > 787) {
        setShowMap(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setShowMap]);


  // 4. mobile navigation height adjustment
  useEffect(() => {
    if (isMobile) {
      const measureMobileNavHeight = () => {
        const mobileNavContainer = document.querySelector(`.${HeaderStyle["mobile-header-container"]}`);
        
        if (mobileNavContainer) {
          const isHidden = mobileNavContainer.classList.contains(HeaderStyle["hidden"]);
          if (isHidden) {
            setMobileNavHeight(85);
          } else {
            const height = mobileNavContainer.offsetHeight;
            console.log('Mobile nav container found, height:', height, 'className:', mobileNavContainer.className);
            setMobileNavHeight(height + 20);
          }
        } else {
          console.log('Mobile nav container not found, using fallback'); 
          setMobileNavHeight(85); 
        }
      };

      setTimeout(measureMobileNavHeight, 500);
      
      window.addEventListener('resize', measureMobileNavHeight);
      window.addEventListener('scroll', measureMobileNavHeight, { passive: true });
      
      return () => {
        window.removeEventListener('resize', measureMobileNavHeight);
        window.removeEventListener('scroll', measureMobileNavHeight);
      };
    }
  }, [isMobile, showMap]); 

  // 5. Sync categoryId from URL if it changes
  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    const urlCategory = params.get('category');
    if (urlCategory && urlCategory !== currentCategory) {
      setCurrentCategory(urlCategory);
    }
  }, [locationObj.search, currentCategory]);

  // 6. Ensure page and category name are always in the URL
  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    let needsUpdate = false;
    if (!params.get('page')) {
      params.set('page', '1');
      needsUpdate = true;
    }
    // Use category name from loaded category data if available
    let categoryName = category?.data?.name || '';
    if (categoryName) {
      categoryName = categoryName.trim().replace(/\s+/g, '-').toLowerCase();
    }
    if (!params.get('category') && categoryName) {
      params.set('category', categoryName);
      needsUpdate = true;
    }
    if (needsUpdate) {
      navigate(`${locationObj.pathname}?${params.toString()}`, { replace: true });
    }
  }, [locationObj, category]);


  return (
   <section className={`${FarmListPageStyle["list-section"]}`}>
      <div className={FarmListPageStyle["list-container"]}>
        <div className={FarmListPageStyle["list-main"]}>
          {/* card grid */}
          {/* farm list  */}
          <div className={FarmListPageStyle["list-card-container"]}
            style={
              // Mobile + showMap ON
                isMobile && showMap
                ? { display: "none" }
                : {}
              }
              >
              <h1 className={FarmListPageStyle["list-title"]}>
                {(() => {
                const name = category?.data?.name;
                if (name) {
                  const words = name.trim().split(" ");
                  const lastWord = words[words.length - 1];
                  if (
                  lastWord.toLowerCase().endsWith("farm") &&
                  !lastWord.toLowerCase().endsWith("farms")
                  ) {
                  words[words.length - 1] = lastWord + "s";
                  return words.join(" ");
                  }
                  return name;
                }
                return "Loading Farms...";
                })()}
              </h1>
              <FarmCardList
                farms={farms}
                style={FarmListPageStyle["list-cards"]}
                loading={loading}
                onFarmClick={farm => {
                const slug = slugify(farm.name || '', { lower: true, strict: true });
                navigate(`/farm/${farm.id}/${slug}`);
              }}
              onFarmHover={(farmId) => setHoveredFarmId(farmId)}
              onFarmUnhover={() => setHoveredFarmId(null)}
            />
            
            {/* Pagination */}
            {!loading && paginationMeta && (
              <Pagination
                currentPage={currentPage}
                totalPages={paginationMeta.pages || 1}
                totalItems={paginationMeta.total || 0}
                itemsPerPage={LIMIT}
                onPageChange={handlePageChange}
                loading={loading}
              />
            )}
          </div>
          {/* end of farm list  */}
          {/* map list  */}
          <div className={FarmListPageStyle["list-map-container"]}
            style={
              isMobile
                ? showMap
                    ? {
                        // showMap = true → show map
                        display: "block",
                        position: "absolute",  
                        top: 0,
                        left: 0,
                        width: "100vw",         
                        height: "100vh",      
                        zIndex: 10 
                      }
                    : { display: "none" } // showMap = false → hide map
                : {} // desktop screen
            }
          >
            <div className={FarmListPageStyle["list-map-img"]}>
              <ListGoogleMapComponent
                markers={markers}
                // userLocation={location}                  
                onLoad={() => {}} // Empty callback since we're not using the map instance
                onFarmSelect={setSelectedFarm}
                onMapBoundsChanged={handleMapBoundsChanged} // Add this prop
                center={(() => {
                  const centerValue = location ? { lat: location.lat, lng: location.lng } : null;
                  return centerValue;
                })()}
                highlightFarmId={hoveredFarmId}  
                mapContainerStyle={{ height: "100%", width: "100%",  borderRadius: "0px" }}
              />
            </div>
          </div>
          {/* end of map list */}
        </div>
      </div>
      {/* Mobile Toggle Button */}
      {isMobile && !selectedFarm && (
          <button
            key={`view-list-btn-${selectedFarm === null}`} 
            className={[
              FarmListPageStyle["toggle-button"],
              animClass && FarmListPageStyle[animClass]
            ].join(" ")}
            style={{
              bottom: isMobile
                ? showMap
                  ? selectedFarm ? '235px' : '25px'
                  : `${mobileNavHeight}px` 
                : '75px'
            }}
            onClick={handleToggle} 
            aria-label={showMap ? "Show List" : "Show Map"}
          >
            <div className={FarmListPageStyle["toggle-button-inner"] }>
              <span className={FarmListPageStyle.toggleButtonLabel}>
                {showMap ? "View List" : "View Map"}
              </span>
            </div>
          </button>
      )}
    </section>
  );
}
